import React from "react";
import { BudgetCategory, BudgetItem, Vendor } from "../../types/budgeting";
import { FileText, ImageIcon, LucideX, Plus, Trash } from "lucide-react";
import VendorDetails from "./VendorDetails";
import BudgetItemComponent from "./BudgetItem";

interface BudgetCategoryItemProps {
  item: BudgetCategory;
  index: number;
  isViewMode: boolean;
  isUpdateMode?: boolean;
  updateCategoryField: (field: keyof BudgetCategory, value: string) => void;
  updateVendorField: (field: keyof Vendor, value: string) => void;
  updateItemField: (
    itemIndex: number,
    field: keyof BudgetItem,
    value: string
  ) => void;
  addItem: () => void;
  deleteItem: (itemIndex: number) => void;
  deleteBudgetItem: () => void;
  addBudgetItem: () => void;
  validationErrors?: { [key: string]: boolean };
  totalCategories: number;
  files: (File | string)[];
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (fileIndex: number) => void;
  uploading?: boolean;
  progress?: number;
  error?: string | null;
}

const BudgetCategoryItem: React.FC<BudgetCategoryItemProps> = ({
  item,
  index,
  isViewMode,
  isUpdateMode = false,
  updateCategoryField,
  updateVendorField,
  updateItemField,
  addItem,
  deleteItem,
  deleteBudgetItem,
  addBudgetItem,
  validationErrors = {},
  totalCategories,
  files,
  handleFileChange,
  removeFile,
  uploading,
  progress,
  error,
}) => {
  const isOnlyCategory = totalCategories === 1;
  const isLastCategory = index === totalCategories - 1;

  const getFileIcon = (fileType: string) => {
    if (!fileType) return <FileText size={16} />;
    if (fileType.includes("image")) return <ImageIcon size={16} />;
    if (fileType.includes("pdf")) return <FileText size={16} />;
    if (fileType.includes("word")) return <FileText size={16} />;
    return <FileText size={16} />;
  };

  const getFileType = (file: File | string) => {
    if (typeof file === "string") {
      const extension = file.split(".").pop()?.toLowerCase();
      if (["jpg", "jpeg", "png", "gif"].includes(extension || "")) {
        return "image/jpeg";
      }
      if (["pdf"].includes(extension || "")) {
        return "application/pdf";
      }
      if (["doc", "docx"].includes(extension || "")) {
        return "application/msword";
      }
      return "application/octet-stream";
    }
    return file.type;
  };

  const getFileName = (file: File | string) => {
    return typeof file === "string"
      ? file.split("/").pop() || "Uploaded file"
      : file.name;
  };

  return (
    <div className="mb-6 last:mb-0">
      <div className="bg-white shadow-md border border-gray-200">
        {/* Budget Category Header Section */}
        <div className="bg-gray-300 px-6 py-3 border-b border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <h2 className="font-semibold text-gray-700">Budget Category</h2>
              <div className="relative w-full md:w-64">
                <select
                  id={`category-${index}`}
                  className={`w-full border ${
                    validationErrors[`category-${index}`]
                      ? "border-red-500"
                      : "border-gray-300"
                  } rounded-md p-2 pr-8 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
                    isViewMode ? "bg-gray-100" : ""
                  }`}
                  value={item.category}
                  onChange={(e) =>
                    updateCategoryField("category", e.target.value)
                  }
                  disabled={isViewMode}
                >
                  <option value="Select Category">Select Category</option>
                  <option value="Technology">Technology</option>
                  <option value="Medical equipment">Medical equipment</option>
                  <option value="Manpower">Manpower</option>
                  <option value="Equipment">Equipment</option>
                  <option value="Training">Training</option>
                  <option value="Others">Others</option>
                </select>
                {validationErrors[`category-${index}`] && !isViewMode && (
                  <p className="text-red-500 text-xs mt-1">
                    Category is required
                  </p>
                )}
              </div>
            </div>
            {!isViewMode && (
              <div className="flex gap-2">
                {isLastCategory && (
                  <button
                    onClick={addBudgetItem}
                    className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-1 w-8 h-8 flex items-center justify-center shadow-sm transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                )}
                {!isOnlyCategory && (
                  <button
                    onClick={deleteBudgetItem}
                    className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1 w-8 h-8 flex items-center justify-center shadow-sm transition-colors"
                  >
                    <Trash size={18} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Items Section */}
        <div className="p-6 pb-0">
          {item.items.map((budgetItem: BudgetItem, itemIndex: number) => (
            <BudgetItemComponent
              key={budgetItem.id || itemIndex}
              item={budgetItem}
              index={itemIndex}
              isViewMode={isViewMode}
              isUpdateMode={isUpdateMode}
              updateItemField={(field, value) =>
                updateItemField(itemIndex, field, value)
              }
              deleteItem={() => deleteItem(itemIndex)}
              validationErrors={validationErrors}
              categoryIndex={index}
              totalItems={item.items.length}
              addItem={addItem}
            />
          ))}
        </div>

        {/* Upload Files */}
        <div className="md:col-span-3 p-6">
          <span className="flex justify-between">
            <label
              htmlFor={`file-upload-${index}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Upload Files
            </label>
            <p className="text-sm text-gray-600 mb-2">
              Supported file formats:{" "}
              <span className="text-orange-600">
                DOC/DOCX, PDF, TXT, JPG, PNG
              </span>{" "}
              Max file size: <span className="text-orange-600">5 MB</span>
            </p>
          </span>
          <label
            htmlFor={`file-upload-${index}`}
            className="block border-2 border-dashed border-gray-400 p-6 text-center text-blue-700 cursor-pointer hover:bg-blue-50"
          >
            {files && files.length > 0 ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-3">
                  {files.map((file, fileIndex) => (
                    <div
                      key={fileIndex}
                      className="text-gray-800 bg-gray-100 px-3 py-1 rounded text-sm flex justify-between items-center"
                    >
                      <div className="flex items-center gap-2">
                        {getFileIcon(getFileType(file))}
                        <span className="truncate w-3/4 text-left">
                          {getFileName(file)}
                        </span>
                      </div>
                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(fileIndex);
                          }}
                          className="text-red-500 hover:text-red-600"
                        >
                          <LucideX size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {uploading && (
                  <div className="text-sm text-gray-600">
                    Uploading... {progress}%
                  </div>
                )}
              </div>
            ) : (
              "Drag & Drop Your files here Or Click"
            )}
          </label>
          <input
            id={`file-upload-${index}`}
            type="file"
            className="hidden"
            accept=".doc,.docx,.pdf,.txt,.jpg,.jpeg,.png"
            multiple
            onChange={handleFileChange}
            disabled={isViewMode}
          />
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>

        {/* Vendor Details Section */}
        <div className="p-6 pt-2 rounded-b-lg">
          <VendorDetails
            vendor={item.vendor}
            updateVendorField={updateVendorField}
            isViewMode={isViewMode}
          />
        </div>
      </div>
    </div>
  );
};

export default BudgetCategoryItem;
